/**
 * Plan-limit hooks — derived from usePlanLimits() for use in feature components.
 *
 * featureEnabled default: gates ONLY DISABLE (absent key = feature is enabled).
 * This matches the pure helper in supabase/functions/_shared/plan-limits.ts.
 * We replicate the check inline below to avoid cross-tree import issues, but
 * the semantics are identical: only an explicit `false` value disables a feature.
 */

import type { LimitMetric } from "./types";
import { usePlanLimits } from "./api";
import { _featureEnabled, _featureAddonEnabled } from "./predicates";
import { useOrganization } from "@/contexts/OrganizationContext";

// Re-export pure helpers so existing imports from "./hooks" keep working.
// New code is free to import from "./predicates" directly.
export { _featureEnabled, _featureAddonEnabled };

// ---------------------------------------------------------------------------
// useFeatureGate
// ---------------------------------------------------------------------------

/**
 * Returns whether a named feature is enabled for the current org.
 *
 * Gates only DISABLE: if a key is absent from feature_gates the feature is
 * considered enabled (default-open). Only an explicit `false` value disables.
 * This makes new features automatically available to all orgs; resellers
 * restrict access by setting `feature_key: false` in the gates object.
 *
 * @param key — the feature gate key to check (e.g. "custom_branding", "sso")
 */
export function useFeatureGate(key: string): { enabled: boolean; loading: boolean } {
  const { data: row, isLoading } = usePlanLimits();

  // No row → no restrictions → enabled; loading → optimistically enabled
  const enabled = _featureEnabled(row?.feature_gates ?? null, key);

  return { enabled, loading: isLoading };
}

// ---------------------------------------------------------------------------
// useFeatureAddon
// ---------------------------------------------------------------------------

/**
 * Returns whether a named opt-in addon is enabled for the current org.
 *
 * OPPOSITE semantics from useFeatureGate:
 *   - Default-closed: absent key OR null row → DISABLED.
 *   - Only an explicit `true` value enables.
 *   - Loading state ALSO defaults to disabled so opt-in module nav doesn't
 *     flash on for tenants without the addon while the plan-limits query
 *     is in flight.
 *
 * Agency owners turn this on per client org via
 * relay_agency_set_client_feature_addon.
 *
 * @param key — addon key, matched against FEATURE_ADDONS
 */
export function useFeatureAddon(key: string): { enabled: boolean; loading: boolean } {
  // usePlanLimits is .enabled-gated on organizationId, so its isLoading flag
  // stays false during the org-loading window. Without consulting org-loading
  // directly, FeatureAddonRoute would 302 to /dashboard on a cold load of an
  // addon route for tenants that legitimately have the addon enabled, since
  // enabled=false slips through before the plan row is fetched.
  // Codex PR #2627 round 3 P2 fix.
  const { isLoading: orgLoading } = useOrganization();
  const { data: row, isLoading: planLoading } = usePlanLimits();
  const loading = orgLoading || planLoading;
  const enabled = !loading && _featureAddonEnabled(row?.feature_addons ?? null, key);
  return { enabled, loading };
}

// ---------------------------------------------------------------------------
// usePlanUsage
// ---------------------------------------------------------------------------

/**
 * Returns usage stats for a named limit metric.
 *
 * @param metric — one of contacts | seats | email_sends_month | automations | sequences
 * @returns
 *   - usage:  current count (0 if no row)
 *   - limit:  max allowed (null = unlimited)
 *   - over:   true when usage >= limit (unlimited → always false)
 *   - pct:    0–100+ percentage of limit used (unlimited → 0; capped below at 0)
 */
export function usePlanUsage(metric: LimitMetric): {
  usage: number;
  limit: number | null;
  over: boolean;
  pct: number;
  loading: boolean;
} {
  const { data: row, isLoading } = usePlanLimits();

  if (!row) {
    return { usage: 0, limit: null, over: false, pct: 0, loading: isLoading };
  }

  const usageKey = `usage_${metric}` as keyof typeof row;
  const limitKey = `max_${metric}` as keyof typeof row;

  const usage = (row[usageKey] as number) ?? 0;
  const limit = (row[limitKey] as number | null) ?? null;

  if (limit === null) {
    // Unlimited — pct is meaningless, report 0; over is always false
    return { usage, limit: null, over: false, pct: 0, loading: isLoading };
  }

  const over = usage >= limit;
  const pct = limit > 0 ? Math.max(0, (usage / limit) * 100) : 100;

  return { usage, limit, over, pct, loading: isLoading };
}
